/**
 * MCP Server Detail Page.
 *
 * Shows full server details with tabs for: Overview, Tools, Settings.
 * Allows editing, testing connection, discovering tools, and managing credentials.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Icon,
  Spinner,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui';
import {
  useMCPServer,
  useMCPServerTools,
  useUpdateMCPServer,
  useDeleteMCPServer,
  useTestMCPConnection,
  useDiscoverMCPTools,
  useCredentialMetadata,
  useDeleteCredentials,
  useUpdateMCPServerCredentials,
} from '@/features/mcp';
import type { MCPHealthStatus, MCPServerStatus, MCPToolResponse, MCPServerCredentials } from '@/features/mcp';
import { toast } from 'sonner';

// =============================================================================
// Helpers
// =============================================================================

function healthBadge(health: MCPHealthStatus) {
  const map = {
    healthy: 'healthy',
    degraded: 'warning',
    unhealthy: 'error',
    unknown: 'default',
  } as const;
  return map[health] ?? 'default';
}

function statusBadge(status: MCPServerStatus) {
  const map = { active: 'active', disabled: 'draft', error: 'error' } as const;
  return map[status] ?? ('default' as const);
}

function tierLabel(tier: string) {
  switch (tier) {
    case 'platform_http': return 'Platform HTTP';
    case 'byos': return 'BYOS (Bring Your Own Server)';
    case 'local_agent': return 'Runner';
    case 'platform_native': return 'Internal (Platform Native)';
    default: return tier;
  }
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// =============================================================================
// Component
// =============================================================================

export function MCPServerDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: server, isLoading, error } = useMCPServer(id || '');
  const { data: toolsData } = useMCPServerTools(id || '');
  const updateMutation = useUpdateMCPServer(id || '');
  const deleteMutation = useDeleteMCPServer();
  const testMutation = useTestMCPConnection(id || '');
  const discoverMutation = useDiscoverMCPTools(id || '');
  const { data: credentialMeta, isLoading: credLoading } = useCredentialMetadata(id || '');
  const deleteCredMutation = useDeleteCredentials(id || '');

  const [activeTab, setActiveTab] = useState('overview');

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleTestConnection = async () => {
    try {
      const result = await testMutation.mutateAsync();
      if (result.success) {
        toast.success(
          t('admin.integrations.testSuccess', {
            version: result.protocol_version || 'unknown',
            tools: result.tools_count,
            latency: result.latency_ms,
          })
        );
      } else {
        toast.error(result.error || t('admin.integrations.testFailed'));
      }
    } catch {
      toast.error(t('admin.integrations.testFailed'));
    }
  };

  const handleDiscoverTools = async () => {
    try {
      const result = await discoverMutation.mutateAsync();
      toast.success(
        t('admin.integrations.toolsDiscovered', { count: result.total })
      );
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleToggleStatus = async () => {
    if (!server) return;
    const newStatus = server.status === 'active' ? 'disabled' : 'active';
    try {
      await updateMutation.mutateAsync({ status: newStatus });
      toast.success(
        newStatus === 'active'
          ? t('admin.integrations.enabled')
          : t('admin.integrations.disabled')
      );
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!server || !window.confirm(t('common.confirmDelete'))) return;
    try {
      await deleteMutation.mutateAsync(server.id);
      toast.success(`${server.name} deleted`);
      navigate('/integrations');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm(t('admin.integrations.credentialDeleteConfirm'))) return;
    try {
      await deleteCredMutation.mutateAsync();
      toast.success(t('admin.integrations.credentialDeleted'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / Error
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !server) {
    return (
      <div className="p-6">
        <Card className="p-6 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">
            {t('admin.integrations.notFound')}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/integrations')}
          >
            {t('common.back')}
          </Button>
        </Card>
      </div>
    );
  }

  const tools: MCPToolResponse[] = toolsData?.tools ?? [];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <button
          onClick={() => navigate('/integrations')}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <Icon name="arrow-left" size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {server.name}
        </h1>
        <StatusBadge status={statusBadge(server.status)} label={server.status} />
        {server.setup_source && server.setup_source !== 'manual' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            {t(`admin.integrations.setupSourceLabel.${server.setup_source}`)}
          </span>
        )}
      </div>
      {server.description && (
        <p className="text-gray-500 dark:text-gray-400 mb-4 ml-7">
          {server.description}
        </p>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 mb-6 ml-7">
        <Button
          variant="outline"
          size="sm"
          leftIcon="zap"
          onClick={handleTestConnection}
          disabled={testMutation.isPending}
        >
          {testMutation.isPending ? t('common.loading') : t('admin.integrations.testConnection')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          leftIcon="refresh-cw"
          onClick={handleDiscoverTools}
          disabled={discoverMutation.isPending}
        >
          {discoverMutation.isPending
            ? t('common.loading')
            : t('admin.integrations.discoverTools')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleToggleStatus}
        >
          {server.status === 'active'
            ? t('admin.integrations.disable')
            : t('admin.integrations.enable')}
        </Button>
        <div className="flex-1" />
        {server.setup_source !== 'auto_enroll' && (
          <Button
            variant="destructive"
            size="sm"
            leftIcon="trash-2"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            {t('admin.integrations.tabOverview')}
          </TabsTrigger>
          <TabsTrigger value="tools">
            {t('admin.integrations.tabTools')} ({server.tools_count})
          </TabsTrigger>
          <TabsTrigger value="settings">
            {t('admin.integrations.tabSettings')}
          </TabsTrigger>
          <TabsTrigger value="update-credentials">
            {t('admin.integrations.tabCredentials')}
          </TabsTrigger>
        </TabsList>

        {/* ===== Overview Tab ===== */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Connection info */}
            <Card className="p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t('admin.integrations.connectionInfo')}
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('admin.integrations.endpointUrl')}</dt>
                  <dd className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded max-w-[280px] truncate">
                    {server.endpoint_url || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('admin.integrations.authType')}</dt>
                  <dd>{server.auth_type}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('admin.integrations.tier')}</dt>
                  <dd>{tierLabel(server.execution_tier)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('admin.integrations.protocol')}</dt>
                  <dd>{server.mcp_protocol_version || '—'}</dd>
                </div>
                {server.template_id && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t('admin.integrations.template')}</dt>
                    <dd>{server.template_id}</dd>
                  </div>
                )}
              </dl>
            </Card>

            {/* Health info */}
            <Card className="p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t('admin.integrations.health')}
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <dt className="text-gray-500">{t('common.status')}</dt>
                  <dd>
                    <StatusBadge
                      status={healthBadge(server.health_status)}
                      label={server.health_status}
                    />
                  </dd>
                </div>
                {server.health_message && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t('admin.integrations.message')}</dt>
                    <dd className="text-xs">{server.health_message}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('admin.integrations.lastCheck')}</dt>
                  <dd>{formatDateTime(server.last_health_check)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('admin.integrations.tools')}</dt>
                  <dd className="font-medium">{server.tools_count}</dd>
                </div>
              </dl>
            </Card>

            {/* Metadata */}
            <Card className="p-4 md:col-span-2">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t('admin.integrations.metadata')}
              </h3>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">ID</dt>
                  <dd className="font-mono text-xs">{server.id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('admin.integrations.serverType')}</dt>
                  <dd>{server.server_type}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('admin.integrations.created')}</dt>
                  <dd>{formatDateTime(server.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('admin.integrations.updated')}</dt>
                  <dd>{formatDateTime(server.updated_at)}</dd>
                </div>
                {server.setup_source && (
                  <div>
                    <dt className="text-gray-500">{t('admin.integrations.setupSource')}</dt>
                    <dd>{String(t(`admin.integrations.setupSourceLabel.${server.setup_source}`))}</dd>
                  </div>
                )}
                {server.execution_tier === 'local_agent' && Boolean((server.config as Record<string, unknown>)?.agent_id) && (
                  <div>
                    <dt className="text-gray-500">{t('admin.integrations.internalAgentId')}</dt>
                    <dd className="font-mono text-xs text-gray-600 dark:text-gray-400">{String((server.config as Record<string, unknown>)?.agent_id)}</dd>
                  </div>
                )}
              </dl>
            </Card>

            {/* Capabilities */}
            {Object.keys(server.capabilities).length > 0 && (
              <Card className="p-4 md:col-span-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  {t('admin.integrations.capabilities')}
                </h3>
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto">
                  {JSON.stringify(server.capabilities, null, 2)}
                </pre>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ===== Tools Tab ===== */}
        <TabsContent value="tools">
          <div className="mt-4 space-y-3">
            {tools.length === 0 ? (
              <Card className="p-8 text-center">
                <Icon name="settings" size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  {t('admin.integrations.noTools')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={handleDiscoverTools}
                  disabled={discoverMutation.isPending}
                >
                  {t('admin.integrations.discoverTools')}
                </Button>
              </Card>
            ) : (
              tools.map((tool) => (
                <Card key={tool.name} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                      <Icon name="settings" size={16} className="text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm">{tool.name}</h4>
                      {tool.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {tool.description}
                        </p>
                      )}
                      {Object.keys(tool.parameters).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                            {t('admin.integrations.showParameters')}
                          </summary>
                          <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(tool.parameters, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* ===== Settings Tab ===== */}
        <TabsContent value="settings">
          <div className="mt-4 space-y-4">
            {/* Policies */}
            {Object.keys(server.policies).length > 0 && (
              <Card className="p-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  {t('admin.integrations.policies')}
                </h3>
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto">
                  {JSON.stringify(server.policies, null, 2)}
                </pre>
              </Card>
            )}

            {/* Credentials */}
            <Card className="p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t('admin.integrations.credentials')}
              </h3>
              {credLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Spinner size="sm" /> {t('common.loading')}
                </div>
              ) : credentialMeta?.has_credentials ? (
                <div className="space-y-3">
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <dt className="text-gray-500">{t('common.status')}</dt>
                      <dd>
                        <StatusBadge status="active" label={t('admin.integrations.credentialStored')} />
                      </dd>
                    </div>
                    {credentialMeta.auth_type && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('admin.integrations.authType')}</dt>
                        <dd className="capitalize">{credentialMeta.auth_type}</dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt className="text-gray-500">{t('admin.integrations.credentialKeys')}</dt>
                      <dd className="flex flex-wrap gap-1 justify-end">
                        {credentialMeta.credential_keys.map((key) => (
                          <span
                            key={key}
                            className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
                          >
                            {key}
                          </span>
                        ))}
                      </dd>
                    </div>
                    {credentialMeta.is_user_scoped && (
                      <div className="flex justify-between items-center">
                        <dt className="text-gray-500">{t('admin.integrations.credentialScope')}</dt>
                        <dd className="text-xs text-amber-600 dark:text-amber-400">
                          {t('admin.integrations.credentialUserScoped')}
                        </dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt className="text-gray-500">{t('admin.integrations.credentialUpdatedAt')}</dt>
                      <dd>{formatDateTime(credentialMeta.updated_at)}</dd>
                    </div>
                  </dl>
                  <div className="flex gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon="shield"
                      onClick={() => setActiveTab('update-credentials')}
                    >
                      {t('admin.integrations.credentialUpdate')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      leftIcon="trash-2"
                      onClick={handleDeleteCredentials}
                      disabled={deleteCredMutation.isPending}
                    >
                      {t('admin.integrations.credentialDelete')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400">
                    {t('admin.integrations.credentialNone')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon="shield"
                    onClick={() => setActiveTab('update-credentials')}
                  >
                    {t('admin.integrations.credentialAdd')}
                  </Button>
                </div>
              )}
            </Card>

            {/* Danger zone */}
            {server.setup_source !== 'auto_enroll' && (
              <Card className="p-4 border-red-200 dark:border-red-800">
                <h3 className="text-sm font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-3">
                  {t('admin.integrations.dangerZone')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  {t('admin.integrations.deleteWarning')}
                </p>
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                  {t('admin.integrations.deleteServer')}
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ===== Update Credentials Tab ===== */}
        <TabsContent value="update-credentials">
          <CredentialUpdateForm
            serverId={server.id}
            authType={server.auth_type}
            onSuccess={() => setActiveTab('settings')}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =============================================================================
// Credential Update Form (inline sub-component)
// =============================================================================

const AUTH_FIELDS: Record<string, Array<{ key: keyof MCPServerCredentials; label: string; type?: string }>> = {
  bearer: [{ key: 'bearer_token', label: 'Bearer Token', type: 'password' }],
  api_key: [{ key: 'api_key', label: 'API Key', type: 'password' }],
  mtls: [
    { key: 'client_cert', label: 'Client Certificate (PEM)' },
    { key: 'client_key', label: 'Client Key (PEM)', type: 'password' },
    { key: 'ca_cert', label: 'CA Certificate (PEM)' },
  ],
  oauth2_cc: [
    { key: 'oauth2_client_id', label: 'Client ID' },
    { key: 'oauth2_client_secret', label: 'Client Secret', type: 'password' },
    { key: 'oauth2_token_url', label: 'Token URL' },
  ],
};

function CredentialUpdateForm({
  serverId,
  authType,
  onSuccess,
}: {
  serverId: string;
  authType: string;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const updateCredMutation = useUpdateMCPServerCredentials(serverId);
  const [values, setValues] = useState<Record<string, string>>({});

  const fields = AUTH_FIELDS[authType] ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Filter out empty values
    const nonEmpty = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v.trim() !== '')
    ) as MCPServerCredentials;
    if (Object.keys(nonEmpty).length === 0) return;

    try {
      await updateCredMutation.mutateAsync(nonEmpty);
      toast.success(t('admin.integrations.credentialSaved'));
      setValues({});
      onSuccess();
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (authType === 'none') {
    return (
      <Card className="p-6 mt-4 text-center">
        <Icon name="shield" size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-gray-500 dark:text-gray-400">
          {t('admin.integrations.credentialNoAuth')}
        </p>
      </Card>
    );
  }

  if (fields.length === 0) {
    return (
      <Card className="p-6 mt-4 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          {t('admin.integrations.credentialUnknownType', { type: authType })}
        </p>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
          {t('admin.integrations.credentialUpdateTitle')}
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          {t('admin.integrations.credentialsHint')}
        </p>
        <div className="space-y-3">
          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label}
              </label>
              {field.key === 'client_cert' || field.key === 'ca_cert' ? (
                <textarea
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  rows={4}
                  placeholder={`-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----`}
                  value={values[field.key] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              ) : (
                <input
                  type={field.type ?? 'text'}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  value={values[field.key] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              )}
            </div>
          ))}
        </div>
      </Card>
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={updateCredMutation.isPending || Object.values(values).every((v) => !v.trim())}
        >
          {updateCredMutation.isPending ? t('common.loading') : t('admin.integrations.credentialSave')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSuccess}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
